import { ref } from 'vue'
import { ConnectionState } from 'livekit-client'
import type { ConnectToRoomOptions } from '@/types/LivekitContext.ts'
import type { LivekitConnectResponse, LivekitRoomStatus } from './types'
import { useLivekitCore, DIABOLOCOM_RTC_CONFIG } from './useLivekitCore'
import { useLivekitContext } from './useLivekitContext'
import { hydrateContextSnapshot } from './hydrateContextSnapshot'
import fetchApi from '@/utils/fetchApi.ts'
import { cleanupLivekitAudio } from './useLivekitAudio'
import { captureSentryException } from '@/utils/sentry'
import type { ColdMonitoringCallContext } from 'shared'
export const useLivekitConnection = () => {
  const { room, connected, currentRoomName, disconnectedRooms, connectionError } = useLivekitCore()
  const error = ref<string | null>(null)
  const contextComposable = useLivekitContext()
  const { resetContext, markContextFetchDone } = contextComposable
  const endCallLoading = ref(false)

  const getRoomStatus = async (roomName: string): Promise<LivekitRoomStatus> => {
    try {
      const response = await fetchApi<{ active: boolean }>(
        {
          endpoint: '/api/livekit/room-status',
          method: 'POST',
          body: { roomName },
        },
        { suppressAlert: true },
      )

      return response.active ? 'active' : 'ended'
    } catch (err) {
      console.error('❌ Failed to check LiveKit room status:', err)
      void captureSentryException(err, {
        tags: { component: 'livekit', operation: 'room-status' },
        extra: { roomName },
      })
      return 'unavailable'
    }
  }

  const connectToRoom = async (options: ConnectToRoomOptions = {}) => {
    const { roomName = '', autoSubscribe = true } = options

    if (room.state === ConnectionState.Connected) {
      return
    }

    resetContext()
    connectionError.value = false

    try {
      const response = await fetchApi<LivekitConnectResponse>({
        endpoint: '/api/livekit/connect',
        method: 'POST',
        body: { roomName },
      })

      if (!response.accessToken || !response.wsUrl) {
        throw new Error('Invalid response from LiveKit BFF')
      }

      const { wsUrl, accessToken } = response

      await Promise.race([
        room.connect(wsUrl, accessToken, {
          autoSubscribe,
          rtcConfig: DIABOLOCOM_RTC_CONFIG,
        }),
        new Promise((_, reject) =>
          setTimeout(() => {
            reject(new Error('LiveKit connection timeout'))
          }, 15_000),
        ),
      ])
      connected.value = true
      connectionError.value = false
      currentRoomName.value = room.name

      const contextLoaded = await getContext()
      if (!contextLoaded) {
        connectionError.value = true
      }
    } catch (err) {
      console.error('❌ Failed to connect to LiveKit:', err)
      void captureSentryException(err, {
        tags: { component: 'livekit', operation: 'connect' },
        extra: { roomName },
      })
      error.value = (err as Error).message
      connectionError.value = true
      markContextFetchDone()
    }
  }

  const disconnectFromRoom = async (hardDisconnect = false, endCall = false) => {
    cleanupLivekitAudio()

    // Check if agent is still present before disconnecting
    // If no agent, the call has ended and we should mark it as disconnected
    if (room.state === ConnectionState.Connected) {
      const agentIdentity = findAgentIdentity()
      const roomName = room.name
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (endCall || !agentIdentity) {
        disconnectedRooms.value.add(roomName)
      }

      await room.disconnect()
    }

    connected.value = false
    if (hardDisconnect) {
      resetContext()
      connectionError.value = false
    }
  }

  const findAgentIdentity = () => {
    let agentIdentity: string | null = null

    room.remoteParticipants.forEach((p) => {
      if (p.identity === room.localParticipant.identity) return
      if (p.identity.startsWith('agent-')) agentIdentity = p.identity
    })

    return agentIdentity
  }

  const callAgentRpc = async (method: string, payload: string) => {
    let agentIdentity: string | null = findAgentIdentity()

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!agentIdentity) {
      const maxAttempts = 5
      const intervalMs = 500

      for (let i = 0; i < maxAttempts; i += 1) {
        await new Promise((resolve) => setTimeout(resolve, intervalMs))
        const foundIdentity = findAgentIdentity()
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (foundIdentity) {
          agentIdentity = foundIdentity
          break
        }
      }

      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (!agentIdentity) {
        console.warn('No agent found in room after polling')
        void disconnectFromRoom(true, true)
        return null
      }
    }

    try {
      const res = await room.localParticipant.performRpc({
        destinationIdentity: agentIdentity,
        method,
        payload,
        responseTimeout: 10_000,
      })
      return res
    } catch (err) {
      console.error(`RPC "${method}" FAILED:`, err)
      void captureSentryException(err, {
        tags: { component: 'livekit', operation: 'rpc' },
        extra: { method, roomName: room.name },
      })
      return null
    }
  }

  const getContext = async () => {
    try {
      const res = await callAgentRpc('fetchChatContext', '')
      if (!res) {
        return false
      }
      const parsed: unknown = JSON.parse(res)
      hydrateContextSnapshot(
        contextComposable,
        parsed as ColdMonitoringCallContext | null | undefined,
      )

      return true
    } catch (err) {
      console.error('Failed to fetch context:', err)
      return false
    } finally {
      contextComposable.markContextFetchDone()
    }
  }

  const endCall = async () => {
    const res = await callAgentRpc('end_call', 'supervisor_end')

    if (res) {
      return true
    }

    return false
  }

  return {
    room,
    connected,
    error,
    connectToRoom,
    getRoomStatus,
    disconnectFromRoom,
    currentRoomName,
    endCall,
    endCallLoading,
  }
}
